import { logWithOffset } from "./logWithOffset";
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

window.Cookies = Cookies;

const COOKIE_NAME="checkoutSessionCookie";

// Not a cookie.
function _setCookie(val) {

  let expiryOffset = 24 * 3600 * 1000;
  const data = {
    val,
    expires: +new Date() + expiryOffset
  }
  localStorage.setItem(COOKIE_NAME, JSON.stringify(data));
};

// Still not a cookie.
function _getCookie() {

  // Port over old style cookies to localStorage.
  const legacyVal = Cookies.get(COOKIE_NAME);
  if (legacyVal !== undefined) {
    _setCookie(legacyVal);
    Cookies.remove(COOKIE_NAME);
    return legacyVal;
  }

  const now = +new Date();
  const item = localStorage.getItem(COOKIE_NAME);
  if (item == null) {
    return undefined;
  }

  const data = JSON.parse(item);
  if (!data || !data.expires) {
    return undefined;
  }

  if (now > data.expires) {
    return undefined;
  }
  return data.val;
}


window.resetCookie = () => {
  _setCookie(uuidv4());
  window.location.reload();
}

export default class ApiClient {
  constructor(props) {
    this.props = props;
    this.getSessionId();
  }

  newSession() {
    const newCookie = uuidv4();
    _setCookie(newCookie);
    this.sessionId = newCookie;
    logWithOffset(`NEW SessionID = ${this.sessionId}`);
  }

  getSessionId() {
    if (this.props.forceCartId.length == 36) {
      _setCookie(this.props.forceCartId);
    }
    this.sessionId = _getCookie();

    const hasRecoveryCartId = typeof(this.props.recoveryCartId) === 'string' && this.props.recoveryCartId.length > 5;
    if (this.sessionId === undefined || this.props.resetCookie || hasRecoveryCartId) {
      this.sessionId = uuidv4();
      _setCookie(this.sessionId);
    }
    logWithOffset(`SessionID = ${this.sessionId}`);
  }

  initBraintree(data) {
    return this.apiRequest(`braintree/${this.sessionId}/init`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  initKlarna(data) {
    return this.apiRequest(`klarna/${this.sessionId}/init`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  initAfterpay(data) {
    return this.apiRequest(`afterpay/${this.sessionId}/init`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  localizeAfterpay(data) {
    return this.apiRequest(`afterpay/${this.sessionId}/localize`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  klarnaCheckout(data) {
    return this.apiRequest(`klarna/${this.sessionId}/checkout`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  validateAddress(addr) {
    return this.apiRequest(`addresses/validate`, { "method": "POST", "body": JSON.stringify(addr) }, {});
  }

  getKlaviyoData() {
    return this.apiRequest(`cart/${this.sessionId}/klaviyo_data`);
  }

  saveValues(savedValues) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "save_values", actionData: { savedValues } }) }, {});
  }

  commitApplePay(data) {
    return this.apiRequest(`applepay/${this.sessionId}/commit`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  getApplepaySession(data) {
    return this.apiRequest(`applepay/${this.sessionId}/init`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  addToAttentive(data) {
    return this.apiRequest(`attentive`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  addToPostscript(data) {
    return this.apiRequest(`postscript`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  getPixelData() {
    return this.apiRequest(`cart/${this.sessionId}/pixel_data`);
  }

  // Not necessarily any data...
  sendEvent(data = {}) {
    data.sessionCartId = data.sessionCartId || this.sessionId;
    return this.apiRequest(`events/${this.sessionId}`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  completeStripeIntent(data) {
    return this.apiRequest(`stripe/${this.sessionId}/complete_intent`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  getMeta() {
    return this.apiRequest('meta');
  }

  setupStripeIntent() {
    return this.apiRequest(`stripe/${this.sessionId}/create_intent`);
  }

  idealStripeIntent(data) {
    return this.apiRequest(`stripe/${this.sessionId}/ideal_create_intent`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  bancontactStripeIntent(data) {
    return this.apiRequest(`stripe/${this.sessionId}/bancontact_create_intent`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }


  walletStripeIntent() {
    return this.apiRequest(`stripe/${this.sessionId}/wallet_create_intent`);
  }

  walletCompleteStripeIntent(data) {
    return this.apiRequest(`stripe/${this.sessionId}/wallet_complete_intent`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  walletRequestObject() {
    return this.apiRequest(`stripe/${this.sessionId}/wallet_request_object`);
  }

  walletShipping(data) {
    return this.apiRequest(`stripe/${this.sessionId}/wallet_shipping`, { "method": "POST", "body": JSON.stringify(data) }, {});
  }

  charge(ccData) {
    return this.apiRequest(`cart/${this.sessionId}/charge`, { "method": "POST", "body": JSON.stringify(ccData) }, {});

  }

  setCountry(country) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "set_shipping_zone", actionData: { country } }) }, {});
  }

  initPaypalFast(nextUrl) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "paypal_init", actionData: { nextUrl } }) }, {});
  }


  setNextUrl(nextUrl) {
    return this.apiRequest(`cart/${this.sessionId}/set_next_url`, { "method": "POST", "body": JSON.stringify({ "nextUrl": nextUrl }) }, {});
  }

  confirmPaypal() {
    return this.apiRequest(`paypal/${this.sessionId}/confirm`, { "method": "GET" }, {});
  }


  toggleAddon(variantId) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "toggle_addon", actionData: { variantId } }) }, {});
  }


  getReceipt(props) {
    return this.apiRequest(`cart/${this.sessionId}/receipt`, {}, props);
  }


  getBrowserEvents(props) {
    return this.apiRequest(`browser_events/${this.sessionId}`, {}, props);
  }

  getCart(props) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "show", actionData: { ...props } }) }, {});
  }

  setCart(props) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "update_lineitems", actionData: { ...props } }) }, {});
  }

  acceptUpsell(data) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "accept_upsell", actionData: data }) }, {});
  }

  setVariantQuantities(variantQuantities) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "set_variant_quantities", actionData: { variantQuantities } }) }, {});
  }

  setCurrentVariant(variantId) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "set_primary_variant", actionData: { variantId } }) }, {});
  }

  removeCoupon() {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "remove_coupon", actionData: { } }) }, {});
  }

  applyCoupon(code) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, { "method": "POST", "body": JSON.stringify({ "action": "apply_coupon", actionData: { code } }) }, {});
  }

  updateCheckoutPageParams(checkoutPageParams, forceOverwrite = false) {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, {
      "method": "POST",
      "body": JSON.stringify({
        "action": "update_checkoutpageparams",
        actionData: { checkoutPageParams, forceOverwrite }
      })
    }, {});
  }

  clearCheckoutPageParams() {
    return this.apiRequest(`cart/${this.sessionId}/fast_cart`, {
      "method": "POST",
      "body": JSON.stringify({
        "action": "clear_checkoutpageparams",
        actionData: {}
      })
    }, {});
  }

  async apiRequest(endPoint, opts={}, queryParams={}) {
    const url = new URL(`https://${this.props.apiEndpoint}/v1/${endPoint}`);
    url.search = new URLSearchParams(queryParams).toString();
    const settings = Object.assign({
        method: 'get',
        mode: 'cors',
        headers: {
          'Content-Type': "application/json"
        }
    }, opts);
    //logWithOffset(`API Request ${url}`);
    const response = await fetch(url, settings);
    return await response.json();
  }
}
